import brandMark from '../assets/brand-mark.svg?raw';
import Icon from "../components/Icon";
import { Toast, PageTitle, Btn, Card, Input, Select, Spinner } from "../components";
import { Fragment, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";
import { getDestinoLabel } from "../utils/tenantLabels";

const PERIODS = [
  { value: "7", label: "7 dias" },
  { value: "15", label: "15 dias" },
  { value: "30", label: "30 dias" },
  { value: "all", label: "Todo período" },
  { value: "custom", label: "Personalizado" },
];

const GROUPS = ["Visão geral", "Operação", "Conformidade", "Automação", "Financeiro", "Cadastros"];
const PAGE_SIZE = 50;

const buildReports = (destinoLabel) => [
  { id: "resumo", group: "Visão geral", label: "Resumo operacional", description: "Indicadores do período e pontos de atenção." },
  { id: "otimizacao", group: "Operação", label: "Otimização de região", description: "Regiões com múltiplas programações no mesmo dia." },
  { id: "pendencias", group: "Operação", label: "Pendências operacionais", description: "Rotas com início, assinatura ou finalização aberta." },
  { id: "rotas-feitas", group: "Operação", label: "Rotas operacionais", description: "Registros mestre de rota no período." },
  { id: "ocupacao", group: "Operação", label: "Ocupação das vans", description: "Passageiros por van vs capacidade do veículo." },
  { id: "rotas-motorista", group: "Operação", label: "Rotas por motorista", description: "Ranking de volume por motorista." },
  { id: "comparativo", group: "Operação", label: "Ida vs volta", description: "Comparativo diário entre os trechos." },
  { id: "trilha", group: "Operação", label: `Programações ${destinoLabel}`, description: "Agrupamento pelo número da programação." },
  { id: "assinaturas", group: "Conformidade", label: "Assinaturas por rota", description: "Percentual consolidado por rota e documentos gerados." },
  { id: "assinaturas-detalhe", group: "Conformidade", label: "Assinaturas por passageiro", description: "Status de assinatura de cada passageiro do pedido (visão programática)." },
  { id: "disciplina", group: "Conformidade", label: "Disciplina dos motoristas", description: "Assinatura de início, finalização e KM." },
  { id: "recusas", group: "Conformidade", label: "Recusas detalhadas", description: "Funcionários que recusaram e motivo informado." },
  { id: "km", group: "Conformidade", label: "KM rodados", description: "KM inicial, final e distância." },
  { id: "falhas", group: "Automação", label: "Falhas de automação", description: "Erros de envio e eventos de falha." },
  { id: "comprovantes", group: "Financeiro", label: "Valores de comprovantes", description: "Totais por motorista e região." },
  { id: "faturamento", group: "Financeiro", label: "Faturamento por pedido", description: "Consolidado por programação para cobrança." },
  { id: "funcionarios-ativos", group: "Cadastros", label: "Funcionários ativos", description: `Base atual de funcionários ${destinoLabel}.` },
  { id: "motoristas-ativos", group: "Cadastros", label: "Motoristas ativos", description: "Base atual de motoristas." },
  { id: "funcionarios-periodo", group: "Cadastros", label: "Funcionários novos", description: "Funcionários ativos criados no período." },
  { id: "motoristas-periodo", group: "Cadastros", label: "Motoristas novos", description: "Motoristas ativos criados no período." },
];

const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#x27;",
}[c]));

const todayKey = () => new Date().toISOString().split("T")[0];
const formatPercent = (value) => `${Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const formatCurrency = (value) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
// Mesma regra do backend (parseKmValue em api/server.js): o último separador
// com 1-2 dígitos à direita é decimal; o resto é milhar — "1.234" é 1234 km.
const parseKmValue = (value) => {
  if (value == null || value === "") return null;
  let text = String(value).replace(/[^\d.,-]/g, "");
  if (!text) return null;
  const sepIndex = Math.max(text.lastIndexOf(","), text.lastIndexOf("."));
  if (sepIndex >= 0) {
    const decimais = text.length - sepIndex - 1;
    if (decimais >= 1 && decimais <= 2) {
      text = `${text.slice(0, sepIndex).replace(/[.,]/g, "")}.${text.slice(sepIndex + 1)}`;
    } else {
      text = text.replace(/[.,]/g, "");
    }
  }
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatKmReading = (value) => {
  const parsed = parseKmValue(value);
  return parsed == null ? "—" : parsed.toLocaleString("pt-BR");
};

const formatKm = (value) => (value == null || value === "" ? "—" : `${Number(value).toLocaleString("pt-BR")} km`);
const formatList = (value) => (Array.isArray(value) && value.length ? value.filter(Boolean).join(", ") : "—");
const buildFormatTrecho = (config) => (value) => (
  value === "ida" ? `Ida → ${getDestinoLabel(config)}` : value === "volta" ? "Volta → Residências" : "—"
);
const buildFormatTrechos = (formatTrecho) => (value) => (
  Array.isArray(value) && value.length ? value.map(formatTrecho).join(" + ") : "—"
);

const getPendingLabel = (row) => {
  const items = [];
  if (row.inicio_pendente) items.push("início da rota");
  if (row.finalizacao_pendente) items.push("KM final");
  return items.length ? items.join(" + ") : "sem pendência";
};

const getProgramacaoStatus = (row) => {
  const falhas = Number(row.rotas_com_falha || 0);
  const andamento = Number(row.rotas_em_andamento || 0);
  const total = Number(row.total_rotas || 0);
  const finalizadas = Number(row.rotas_finalizadas || 0);
  if (falhas > 0) return "Com falha";
  if (andamento > 0) return "Em andamento";
  if (total > 0 && finalizadas === total) return "Finalizada";
  return "Pendente";
};

const SIGN_STATUS = {
  assinado: { label: "Assinado", color: "#22c55e" },
  recusado: { label: "Recusado", color: "#ef4444" },
  pendente: { label: "Pendente", color: "#fbbf24" },
  nao_despachado: { label: "Aguardando envio", color: "#94a3b8" },
};
const getSignStatus = (value) => SIGN_STATUS[String(value || "pendente")] || {
  label: String(value || "—").replace(/_/g, " "),
  color: "var(--muted)",
};
// Passageiro do pedido ainda não despachado para nenhuma van (visão programática).
const getSignStatusKey = (row) => (row?.nao_despachado ? "nao_despachado" : (row?.status || "pendente"));
const getDocumentosInsight = (summary) => {
  const pedido = Number(summary.documentos_pedido || 0);
  const operacional = Number(summary.documentos_operacionais || 0);
  if (pedido > 0 || operacional > 0) {
    return {
      value: `${pedido}/${operacional}`,
      text: `${pedido} ${pedido === 1 ? "documento de pedido" : "documentos de pedidos"} e ${operacional} ${operacional === 1 ? "documento de van" : "documentos de vans"} no período.`,
    };
  }

  const documentos = Number(summary.documentos_gerados || 0);
  const rotas = Number(summary.rotas_feitas || 0);
  if (documentos <= rotas) return null;

  const semRota = Number(summary.documentos_sem_rota || 0);
  if (semRota > 0) {
    return {
      value: `${semRota}`,
      text: `${semRota} documento${semRota === 1 ? "" : "s"} sem vínculo com rota explica${semRota === 1 ? "" : "m"} a diferença entre documentos e rotas.`,
    };
  }

  const multiplas = Number(summary.rotas_com_multiplos_documentos || 0);
  if (multiplas > 0) {
    return {
      value: `${multiplas}`,
      text: `${multiplas} rota${multiplas === 1 ? "" : "s"} tem${multiplas === 1 ? "" : "m"} mais de um documento gerado.`,
    };
  }

  const foraRegra = Number(summary.documentos_fora_regra_operacional || 0);
  if (foraRegra > 0) {
    return {
      value: `${foraRegra}`,
      text: `${foraRegra} documento${foraRegra === 1 ? "" : "s"} está${foraRegra === 1 ? "" : "o"} fora da regra de rota operacional.`,
    };
  }

  return {
    value: `${rotas}/${documentos}`,
    text: "Documentos e rotas usam bases diferentes; confira os vínculos dos documentos.",
  };
};

const formatDate = (value) => {
  if (!value) return "—";
  const normalized = String(value).substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return value;
  return normalized.split("-").reverse().join("/");
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  const d = digits.startsWith("55") ? digits.slice(2) : digits;
  if (!d) return "—";
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
};

const formatAddress = (row) => {
  const line1 = [row?.rua, row?.numero].filter(Boolean).join(", ");
  const line2 = [row?.bairro, row?.cidade].filter(Boolean).join(" · ");
  return [line1, line2].filter(Boolean).join(" — ") || "—";
};

function DriverNameCell({ row }) {
  const variants = Array.isArray(row.nomes_variantes)
    ? row.nomes_variantes.filter((item) => item && item !== row.motorista_nome)
    : [];
  return (
    <div>
      <div style={{ fontWeight: 800 }}>{row.motorista_nome || "—"}</div>
      {variants.length > 0 && (
        <div style={{ color: "var(--muted)", fontSize: '0.8125rem', marginTop: 4 }}>
          Unificado: {variants.join(", ")}
        </div>
      )}
    </div>
  );
}

const formatVeiculoLabel = (row) => {
  const nome = String(row?.veiculo_nome || "").trim();
  const placa = String(row?.veiculo_placa || "").trim();
  if (nome && placa) return `${nome} (${placa})`;
  return nome || placa || "—";
};

// Verde: van bem aproveitada; amarelo: espaço sobrando; vermelho: mais da metade vazia.
const getOcupacaoColor = (value) => {
  const percent = Number(value || 0);
  if (percent >= 75) return "#22c55e";
  if (percent >= 50) return "#fbbf24";
  return "#ef4444";
};

function OcupacaoPill({ value }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: '0.875rem',
      fontWeight: 800,
      color: getOcupacaoColor(value),
      background: "color-mix(in srgb, currentColor 14%, transparent)",
      border: "1px solid color-mix(in srgb, currentColor 35%, transparent)",
    }}>{formatPercent(value)}</span>
  );
}

function StatusPill({ status }) {
  const { label, color } = getSignStatus(status);
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: '0.875rem',
      fontWeight: 800,
      color,
      background: "color-mix(in srgb, currentColor 14%, transparent)",
      border: "1px solid color-mix(in srgb, currentColor 35%, transparent)",
    }}>{label}</span>
  );
}

const flattenFalhas = (falhas) => {
  if (!falhas) return [];
  const eventos = (falhas.eventos || []).map((row) => ({
    id: `ev-${row.id}`,
    origem_tipo: "Evento",
    data: row.data,
    id_pedido: row.id_pedido,
    detalhe: row.mensagem || row.tipo,
    etapa: row.tipo,
    quando: row.criado_em,
  }));
  const envios = (falhas.envios_grupos || []).map((row) => ({
    id: `env-${row.id}`,
    origem_tipo: "Envio",
    data: row.data,
    id_pedido: row.id_pedido,
    detalhe: row.erro_mensagem,
    etapa: row.erro_etapa,
    quando: row.ultima_tentativa_em,
  }));
  return [...eventos, ...envios].sort((a, b) => new Date(b.quando || 0) - new Date(a.quando || 0));
};

const reportRows = (id, report, falhasRows) => {
  if (!report) return [];
  const map = {
    otimizacao: report.sobreposicao_regiao || [],
    pendencias: report.pendencias_operacionais || [],
    "rotas-feitas": report.rotas || [],
    ocupacao: report.ocupacao_vans || [],
    faturamento: report.faturamento_pedidos || [],
    "rotas-motorista": report.motoristas_rotas || [],
    comparativo: report.comparativo_ida_volta || [],
    trilha: report.trilha_pedidos || [],
    assinaturas: report.assinaturas_rotas || [],
    "assinaturas-detalhe": report.assinaturas_detalhadas || [],
    disciplina: report.disciplina_motoristas || [],
    recusas: report.recusas_detalhadas || [],
    km: report.km_rodados || [],
    falhas: falhasRows,
    comprovantes: report.valores_comprovantes || [],
    "funcionarios-ativos": report.funcionarios_ativos || [],
    "motoristas-ativos": report.motoristas_ativos || [],
    "funcionarios-periodo": report.funcionarios || [],
    "motoristas-periodo": report.motoristas || [],
  };
  return map[id] || [];
};

function Metric({ label, value, accent = "var(--text)", hint, prominent = false }) {
  return (
    <div style={{
      minHeight: prominent ? 124 : 88,
      padding: prominent ? "18px 20px" : "14px 16px",
      border: "1px solid var(--border)",
      borderRadius: 8,
      background: "transparent",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}>
      <div style={{ fontVariantNumeric: "tabular-nums", fontSize: prominent ? 36 : 24, lineHeight: 1.2, fontWeight: 550, color: accent }}>{value}</div>
      <div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: "var(--text)" }}>{label}</div>
        {hint && <div style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: 4 }}>{hint}</div>}
      </div>
    </div>
  );
}

function Insight({ label, value, text, color }) {
  return (
    <div style={{ padding: "14px 16px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--s2)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <span style={{ color: "var(--muted)", fontSize: '0.8125rem', fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
        <span style={{ color, fontFamily: "var(--font-mono)", fontWeight: 900 }}>{value}</span>
      </div>
      <div style={{ marginTop: 10, fontSize: '0.9375rem', color: "var(--text)", lineHeight: 1.45 }}>{text}</div>
    </div>
  );
}

function DetailGrid({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, padding: "14px 16px", background: "rgba(249,115,22,0.05)", borderRadius: 10 }}>
      {items.map((item) => (
        <div key={item.label}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--muted)", marginBottom: 3 }}>{item.label}</div>
          <div style={{ fontSize: '0.9375rem', color: "var(--text)", lineHeight: 1.5 }}>{item.value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ columns, rows, empty, rowKey, tableKey, renderDetails }) {
  const [page, setPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState(null);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visible = rows.slice(startIndex, startIndex + PAGE_SIZE);
  const firstVisible = rows.length ? startIndex + 1 : 0;
  const lastVisible = Math.min(startIndex + PAGE_SIZE, rows.length);
  const expandable = typeof renderDetails === "function";
  const totalColumns = columns.length + (expandable ? 1 : 0);

  useEffect(() => {
    setPage(1);
    setExpandedKey(null);
  }, [rows, tableKey]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageButtonStyle = (disabled = false) => ({
    minHeight: 48,
    padding: "7px 10px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: disabled ? "rgba(255,255,255,0.03)" : "var(--s1)",
    color: disabled ? "var(--muted)" : "var(--text)",
    fontSize: '0.875rem',
    fontWeight: 800,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  });

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            {expandable && <th style={{ width: 30 }} />}
            {columns.map((column) => (
              <th key={tableKey ? `${tableKey}-${column.key}` : column.key} style={{ padding: "12px 14px", textAlign: column.align || "left", color: "var(--muted)", fontSize: '0.8125rem', fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan={totalColumns} style={{ padding: 34, color: "var(--muted)", textAlign: "center", fontSize: '1rem' }}>{empty}</td>
            </tr>
          )}
          {visible.map((row, index) => {
            const absoluteIndex = startIndex + index;
            const key = rowKey ? rowKey(row, absoluteIndex) : row.id || row.chave || row.motorista_nome || absoluteIndex;
            const isExpanded = expandable && expandedKey === key;
            return (
            <Fragment key={key}>
            <tr
              onClick={expandable ? () => setExpandedKey(isExpanded ? null : key) : undefined}
              style={{
                borderBottom: isExpanded ? "none" : "1px solid var(--border)",
                cursor: expandable ? "pointer" : "default",
                background: isExpanded ? "rgba(249,115,22,0.06)" : "transparent",
                transition: "background .12s ease",
              }}
              onMouseEnter={(e) => { if (expandable && !isExpanded) e.currentTarget.style.background = "rgba(249,115,22,0.06)"; }}
              onMouseLeave={(e) => { if (expandable && !isExpanded) e.currentTarget.style.background = "transparent"; }}
            >
              {expandable && (
                <td style={{ padding: "12px 0 12px 14px", color: "var(--muted)", fontSize: '0.8125rem', verticalAlign: "top" }}>
                  <Btn size="sm" variant="secondary" aria-expanded={isExpanded} aria-label={`${isExpanded ? "Recolher" : "Ver detalhes"} da linha ${absoluteIndex + 1}`} onClick={event => { event.stopPropagation(); setExpandedKey(isExpanded ? null : key); }}>{isExpanded ? "−" : "+"}</Btn>
                </td>
              )}
              {columns.map((column) => (
                <td key={tableKey ? `${tableKey}-${column.key}` : column.key} style={{ padding: "12px 14px", color: column.muted ? "var(--muted)" : "var(--text)", fontSize: '0.9375rem', textAlign: column.align || "left", verticalAlign: "top" }}>
                  {column.render ? column.render(row, absoluteIndex) : row[column.key]}
                </td>
              ))}
            </tr>
            {isExpanded && (
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <td colSpan={totalColumns} style={{ padding: "0 14px 14px" }}>
                  {renderDetails(row)}
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
      </table>
      {rows.length > 0 && (
        <div style={{ padding: "12px 14px", color: "var(--muted)", borderTop: "1px solid var(--border)", fontSize: '0.875rem', display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span>Exibindo {firstVisible}-{lastVisible} de {rows.length}</span>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" disabled={currentPage === 1} onClick={() => setPage(1)} style={pageButtonStyle(currentPage === 1)}>Primeira</button>
              <button type="button" disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))} style={pageButtonStyle(currentPage === 1)}>Anterior</button>
              <span style={{ color: "var(--text)", fontWeight: 800, padding: "0 4px" }}>Página {currentPage} de {totalPages}</span>
              <button type="button" disabled={currentPage === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))} style={pageButtonStyle(currentPage === totalPages)}>Próxima</button>
              <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} style={pageButtonStyle(currentPage === totalPages)}>Última</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DriverChart({ rows }) {
  const topRows = rows.slice(0, 8);
  const max = Math.max(...topRows.map((row) => Number(row.total_rotas || 0)), 1);

  return (
    <div style={{ padding: 18, display: "grid", gap: 10, borderBottom: topRows.length ? "1px solid var(--border)" : "none" }}>
      {topRows.length === 0 && <div style={{ padding: 22, textAlign: "center", color: "var(--muted)" }}>Sem rotas no período.</div>}
      {topRows.map((row) => {
        const width = Math.max(4, (Number(row.total_rotas || 0) / max) * 100);
        return (
          <div key={row.motorista_nome} style={{ display: "grid", gridTemplateColumns: "minmax(150px, 220px) 1fr auto", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.motorista_nome}</div>
            <div style={{ height: 12, background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${width}%`, height: "100%", background: "var(--accent)" }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{row.total_rotas}</div>
          </div>
        );
      })}
    </div>
  );
}

function ReportPicker({ reports, selectedId, onSelect, group, setGroup, report, falhasRows, isMobile, onGroupChange }) {
  if (isMobile) {
    return (
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <Select label="Relatório" value={selectedId} onChange={(e) => onSelect(e.target.value)}>
          {reports.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </Select>
      </Card>
    );
  }

  const items = reports.filter((item) => item.group === group);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8 }}>
        {GROUPS.map((item) => {
          const active = item === group;
          return (
            <button key={item} type="button" onClick={() => onGroupChange(item)} style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: active ? "1px solid rgba(249,115,22,0.45)" : "1px solid var(--border)",
              background: active ? "rgba(249,115,22,0.14)" : "var(--s1)",
              color: active ? "var(--accent)" : "var(--muted)",
              fontSize: '0.9375rem',
              fontWeight: 800,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}>{item}</button>
          );
        })}
      </div>

      {items.length > 1 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 8 }}>
        {items.map((item) => {
          const active = item.id === selectedId;
          const count = item.id === "resumo" ? null : reportRows(item.id, report, falhasRows).length;
          return (
            <button key={item.id} type="button" onClick={() => onSelect(item.id)} style={{
              minHeight: 76,
              padding: "12px 14px",
              borderRadius: 8,
              border: active ? "1px solid rgba(249,115,22,0.5)" : "1px solid var(--border)",
              background: active ? "linear-gradient(135deg, rgba(249,115,22,0.16), rgba(34,197,94,0.06))" : "var(--s1)",
              color: "var(--text)",
              textAlign: "left",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 8,
            }}>
              <span style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: '0.9375rem', lineHeight: 1.25, fontWeight: 900 }}>{item.label}</span>
                {report && count != null && <span style={{ color: active ? "var(--accent)" : "var(--muted)", fontSize: '0.8125rem', fontFamily: "var(--font-mono)" }}>{count}</span>}
              </span>
              <span style={{ color: "var(--muted)", fontSize: '0.875rem', lineHeight: 1.35 }}>{item.description}</span>
            </button>
          );
        })}
      </div>}
    </div>
  );
}

function buildRows(columns, rows) {
  return rows.map((row, index) => (
    `<tr>${columns.map((column) => `<td>${esc(column.export ? column.export(row, index) : row[column.key])}</td>`).join("")}</tr>`
  )).join("");
}

function buildTableHtml(title, columns, rows) {
  return `
    <section>
      <h2>${esc(title)} <span>${rows.length} registro${rows.length === 1 ? "" : "s"}</span></h2>
      <table>
        <thead><tr>${columns.map((column) => `<th>${esc(column.label)}</th>`).join("")}</tr></thead>
        <tbody>${rows.length ? buildRows(columns, rows) : `<tr><td colspan="${columns.length}">Sem registros.</td></tr>`}</tbody>
      </table>
    </section>
  `;
}

const csvCell = (value) => {
  const text = String(value ?? "");
  if (/[";\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

function buildCsv(columns, rows) {
  const header = columns.map((column) => csvCell(column.label)).join(";");
  const body = rows.map((row, index) =>
    columns.map((column) => csvCell(column.export ? column.export(row, index) : row[column.key])).join(";")
  ).join("\r\n");
  return "\uFEFF" + [header, body].join("\r\n");
}

export default function RelatoriosPage() {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { config } = useTenant();
  const destinoLabel = getDestinoLabel(config);
  const REPORTS = useMemo(() => buildReports(destinoLabel), [destinoLabel]);
  const formatTrecho = useMemo(() => buildFormatTrecho(config), [config]);
  const formatTrechos = useMemo(() => buildFormatTrechos(formatTrecho), [formatTrecho]);
  const [periodo, setPeriodo] = useState("30");
  const [inicio, setInicio] = useState(todayKey());
  const [fim, setFim] = useState(todayKey());
  const [selectedId, setSelectedId] = useState("resumo");
  const [group, setGroup] = useState("Visão geral");

  const selectReport = (id) => {
    setSelectedId(id);
    const meta = REPORTS.find((item) => item.id === id);
    if (meta) setGroup(meta.group);
  };

  const changeGroup = (nextGroup) => {
    setGroup(nextGroup);
    const items = REPORTS.filter((item) => item.group === nextGroup);
    if (!items.some((item) => item.id === selectedId)) {
      selectReport(items[0]?.id || "resumo");
    }
  };
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const showToast = (message) => {
    recordUiError({ message, page: "relatorios", source: "toast" });
    setToast(message);
  };

  const loadReport = async () => {
    if (periodo === "custom" && (!inicio || !fim)) {
      showToast("Informe as datas de início e fim.");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ periodo });
      if (periodo === "custom") {
        params.set("inicio", inicio);
        params.set("fim", fim);
      }
      setReport(await apiFetch(`/relatorios/operacionais?${params.toString()}`));
    } catch (e) {
      showToast(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodo !== "custom") loadReport();
  }, [periodo]);

  const falhasRows = useMemo(() => flattenFalhas(report?.falhas_automacao), [report]);
  const selectedMeta = REPORTS.find((item) => item.id === selectedId) || REPORTS[0];
  const summary = report?.resumo || {};
  const documentosInsight = getDocumentosInsight(summary);

  const columns = useMemo(() => ({
    overlap: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "regiao", label: "Região", export: (r) => r.regiao },
      { key: "pedidos", label: "Pedidos", render: (r) => formatList(r.pedidos), export: (r) => formatList(r.pedidos) },
      { key: "motoristas", label: "Motoristas", render: (r) => formatList(r.motoristas), export: (r) => formatList(r.motoristas) },
      { key: "total_passageiros", label: "Passageiros", align: "right", export: (r) => r.total_passageiros },
    ],
    pendencia: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "regiao", label: "Região", export: (r) => r.regiao || "—" },
      { key: "motorista_nome", label: "Motorista", export: (r) => r.motorista_nome || "—" },
      { key: "pendente_em", label: "Falta", render: getPendingLabel, export: getPendingLabel },
      { key: "status_operacional", label: "Status atual", export: (r) => String(r.status_operacional || "—").replace(/_/g, " ") },
    ],
    rota: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "regioes", label: "Regiões", render: (r) => formatList(r.regioes), export: (r) => formatList(r.regioes) },
      { key: "total_passageiros", label: "Passageiros", align: "right", export: (r) => r.total_passageiros },
      { key: "status_operacional", label: "Status", export: (r) => String(r.status_operacional || "—").replace(/_/g, " ") },
    ],
    ocupacao: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "regiao", label: "Região", export: (r) => r.regiao || "—" },
      { key: "motorista_nome", label: "Motorista", export: (r) => r.motorista_nome || "—" },
      { key: "veiculo_nome", label: "Veículo", render: (r) => formatVeiculoLabel(r), export: (r) => formatVeiculoLabel(r) },
      { key: "lotacao", label: "Lotação", align: "right", render: (r) => `${r.total_passageiros}/${r.capacidade}`, export: (r) => `${r.total_passageiros}/${r.capacidade}` },
      { key: "percentual_ocupacao", label: "% ocupação", align: "right", render: (r) => <OcupacaoPill value={r.percentual_ocupacao} />, export: (r) => formatPercent(r.percentual_ocupacao) },
    ],
    faturamento: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Pedido", export: (r) => r.id_pedido || "—" },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "horario", label: "Horário", export: (r) => r.horario || "—" },
      { key: "passageiros_previstos", label: "Previstos", align: "right", export: (r) => r.passageiros_previstos },
      { key: "passageiros_presentes", label: "Presentes", align: "right", export: (r) => r.passageiros_presentes },
      { key: "passageiros_ausentes", label: "Ausentes", align: "right", export: (r) => r.passageiros_ausentes },
      { key: "status_pedido", label: "Status", export: (r) => String(r.status_pedido || "—").replace(/_/g, " ") },
      { key: "valor_monetario", label: "Valor", align: "right", render: (r) => (r.valor_monetario == null ? "—" : formatCurrency(r.valor_monetario)), export: (r) => (r.valor_monetario == null ? "—" : formatCurrency(r.valor_monetario)) },
    ],
    driver: [
      { key: "motorista_nome", label: "Motorista", render: (r) => <DriverNameCell row={r} />, export: (r) => r.motorista_nome },
      { key: "total_rotas", label: "Rotas", align: "right", export: (r) => r.total_rotas },
      { key: "total_documentos", label: "Documentos", align: "right", export: (r) => r.total_documentos },
      { key: "total_passageiros", label: "Passageiros", align: "right", export: (r) => r.total_passageiros },
      { key: "rotas_finalizadas", label: "Finalizadas", align: "right", export: (r) => r.rotas_finalizadas },
    ],
    comparativo: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "rotas_ida", label: "Rotas ida", align: "right", export: (r) => r.rotas_ida },
      { key: "rotas_volta", label: "Rotas volta", align: "right", export: (r) => r.rotas_volta },
      { key: "passageiros_ida", label: "Pass. ida", align: "right", export: (r) => r.passageiros_ida },
      { key: "passageiros_volta", label: "Pass. volta", align: "right", export: (r) => r.passageiros_volta },
    ],
    trilha: [
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido },
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "trechos", label: "Trechos", render: (r) => formatTrechos(r.trechos), export: (r) => formatTrechos(r.trechos) },
      { key: "regioes", label: "Regiões", render: (r) => formatList(r.regioes), export: (r) => formatList(r.regioes) },
      { key: "total_rotas", label: "Vans", align: "right", export: (r) => r.total_rotas },
      { key: "total_passageiros_pedido", label: "Passageiros", align: "right", render: (r) => r.total_passageiros_pedido ?? 0, export: (r) => r.total_passageiros_pedido ?? 0 },
      { key: "status_programacao", label: "Status", render: getProgramacaoStatus, export: getProgramacaoStatus },
    ],
    assinatura: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "total_documentos", label: "Documentos", align: "right", export: (r) => r.total_documentos || 0 },
      { key: "percentual_assinatura", label: "% assinatura", align: "right", render: (r) => formatPercent(r.percentual_assinatura), export: (r) => formatPercent(r.percentual_assinatura) },
      { key: "total_assinados", label: "Assinados", align: "right", export: (r) => r.total_assinados },
      { key: "total_recusados", label: "Recusas", align: "right", export: (r) => r.total_recusados },
      { key: "total_pendentes", label: "Pendentes", align: "right", export: (r) => r.total_pendentes },
    ],
    assinaturaDetalhe: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "trecho", label: "Trecho", render: (r) => formatTrecho(r.trecho), export: (r) => formatTrecho(r.trecho) },
      { key: "regiao", label: "Região", export: (r) => r.regiao || "—" },
      { key: "pessoa_nome", label: "Passageiro", render: (r) => r.pessoa_nome || "—", export: (r) => r.pessoa_nome || "—" },
      { key: "status", label: "Assinatura", render: (r) => <StatusPill status={getSignStatusKey(r)} />, export: (r) => getSignStatus(getSignStatusKey(r)).label },
      { key: "assinado_em", label: "Quando", render: (r) => formatDateTime(r.assinado_em), export: (r) => formatDateTime(r.assinado_em) },
      { key: "observacao_recusa", label: "Motivo", render: (r) => r.observacao_recusa || "—", export: (r) => r.observacao_recusa || "—" },
    ],
    disciplina: [
      { key: "motorista_nome", label: "Motorista", render: (r) => <DriverNameCell row={r} />, export: (r) => r.motorista_nome },
      { key: "total_grupos", label: "Grupos", align: "right", export: (r) => r.total_grupos ?? r.total_rotas },
      { key: "percentual_inicio", label: "% início", align: "right", render: (r) => formatPercent(r.percentual_inicio), export: (r) => formatPercent(r.percentual_inicio) },
      { key: "percentual_finalizacao", label: "% finalização", align: "right", render: (r) => formatPercent(r.percentual_finalizacao), export: (r) => formatPercent(r.percentual_finalizacao) },
      { key: "finalizacao_pendente", label: "Pendências", align: "right", export: (r) => r.finalizacao_pendente },
    ],
    recusa: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "funcionario_nome", label: "Funcionário", export: (r) => r.funcionario_nome },
      { key: "motorista_nome", label: "Motorista", export: (r) => r.motorista_nome || "—" },
      { key: "observacao_recusa", label: "Motivo", export: (r) => r.observacao_recusa || "—" },
      { key: "assinado_em", label: "Quando", render: (r) => formatDateTime(r.assinado_em), export: (r) => formatDateTime(r.assinado_em) },
    ],
    km: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "motorista_nome", label: "Motorista", export: (r) => r.motorista_nome || "—" },
      { key: "km_inicio", label: "KM início", align: "right", render: (r) => formatKmReading(r.km_inicio), export: (r) => formatKmReading(r.km_inicio) },
      { key: "km_final", label: "KM final", align: "right", render: (r) => formatKmReading(r.km_final), export: (r) => formatKmReading(r.km_final) },
      { key: "km_rodados", label: "KM rodados", align: "right", render: (r) => formatKm(r.km_rodados), export: (r) => formatKm(r.km_rodados) },
    ],
    falha: [
      { key: "data", label: "Data", render: (r) => formatDate(r.data), export: (r) => formatDate(r.data) },
      { key: "id_pedido", label: "Programação", export: (r) => r.id_pedido || "—" },
      { key: "origem_tipo", label: "Origem", export: (r) => r.origem_tipo },
      { key: "etapa", label: "Etapa", export: (r) => r.etapa || "—" },
      { key: "detalhe", label: "Detalhe", export: (r) => r.detalhe || "—" },
      { key: "quando", label: "Quando", render: (r) => formatDateTime(r.quando), export: (r) => formatDateTime(r.quando) },
    ],
    valor: [
      { key: "motorista_nome", label: "Motorista", render: (r) => <DriverNameCell row={r} />, export: (r) => r.motorista_nome },
      { key: "regiao", label: "Região", export: (r) => r.regiao },
      { key: "valor_total", label: "Total", align: "right", render: (r) => formatCurrency(r.valor_total), export: (r) => formatCurrency(r.valor_total) },
      { key: "valor_medio", label: "Média", align: "right", render: (r) => formatCurrency(r.valor_medio), export: (r) => formatCurrency(r.valor_medio) },
    ],
    funcionario: [
      { key: "nome", label: "Nome", export: (r) => r.nome },
      { key: "telefone", label: "Telefone", render: (r) => formatPhone(r.telefone), export: (r) => formatPhone(r.telefone) },
      { key: "endereco", label: "Endereço", render: (r) => formatAddress(r), export: (r) => formatAddress(r) },
      { key: "regiao", label: "Região", export: (r) => r.regiao || "—" },
      { key: "criado_em", label: "Cadastro", render: (r) => formatDateTime(r.criado_em), export: (r) => formatDateTime(r.criado_em) },
    ],
    motorista: [
      { key: "nome", label: "Nome", export: (r) => r.nome },
      { key: "telefone", label: "Telefone", render: (r) => formatPhone(r.telefone), export: (r) => formatPhone(r.telefone) },
      { key: "endereco", label: "Endereço", render: (r) => formatAddress(r), export: (r) => formatAddress(r) },
      { key: "criado_em", label: "Cadastro", render: (r) => formatDateTime(r.criado_em), export: (r) => formatDateTime(r.criado_em) },
    ],
  }), [formatTrecho, formatTrechos]);

  // Detalhes exibidos ao expandir a linha (accordion) — campos que a consulta
  // já retorna mas não cabem como coluna. Nada de navegação: tudo inline.
  const rowDetails = {
    pendencias: (r) => [
      { label: "Horário", value: r.horario || "—" },
      { label: "Motorista", value: r.motorista_nome || "—" },
      { label: "Região", value: r.regiao || "—" },
      { label: "Início da rota", value: r.status_inicio === "assinado" ? "assinado" : "pendente" },
      { label: "KM final", value: r.status_finalizacao === "assinado" ? "informado" : "pendente" },
      { label: "Documento", value: r.documento_id ? `#${r.documento_id}` : "—" },
    ],
    "rotas-feitas": (r) => [
      { label: "Horário", value: r.horario || "—" },
      { label: "Vans", value: r.total_grupos ?? "—" },
      { label: "Motoristas", value: formatList(r.motoristas) },
      { label: "Veículos", value: formatList(r.veiculos) },
      { label: "Status da rota", value: String(r.status || "—").replace(/_/g, " ") },
      { label: "Confirmada em", value: formatDateTime(r.confirmado_em) },
      { label: "Último evento", value: formatDateTime(r.ultimo_evento_em) },
      { label: "Criada por", value: r.usuario_nome || "—" },
    ],
    faturamento: (r) => [
      { label: "Assinados", value: r.passageiros_assinados ?? 0 },
      { label: "Recusados", value: r.passageiros_recusados ?? 0 },
      { label: "Documento do pedido", value: r.documento_id ? `#${r.documento_id}` : "sem documento" },
      { label: "Valor", value: r.valor_monetario == null ? "sem valor informado" : formatCurrency(r.valor_monetario) },
    ],
    assinaturas: (r) => [
      { label: "Trecho", value: formatTrecho(r.trecho) },
      { label: "Horário", value: r.horario || "—" },
      { label: "Passageiros", value: r.total_passageiros ?? 0 },
      { label: "Presentes", value: r.total_presentes ?? 0 },
      { label: "Ausentes", value: r.total_ausentes ?? 0 },
      { label: "Motorista assinou", value: r.motorista_assinou ? "sim" : "não" },
      { label: "KM final informado", value: r.finalizacao_assinada ? "sim" : "não" },
      { label: "Motoristas", value: formatList(r.motoristas_documentos || r.motoristas) },
    ],
    recusas: (r) => [
      { label: "Trecho", value: formatTrecho(r.trecho) },
      { label: "Horário", value: r.horario || "—" },
      { label: "Região", value: r.regiao || "—" },
      { label: "Documento", value: r.documento_id ? `#${r.documento_id}` : "—" },
    ],
    km: (r) => [
      { label: "Trecho", value: formatTrecho(r.trecho) },
      { label: "Região", value: r.regiao || "—" },
      { label: "Início assinado", value: r.status_inicio === "assinado" ? "sim" : "não" },
      { label: "KM final assinado", value: r.status_finalizacao === "assinado" ? "sim" : "não" },
      { label: "Documento", value: r.documento_id ? `#${r.documento_id}` : "—" },
    ],
  };

  const reportConfig = {
    otimizacao: { columns: columns.overlap, rows: reportRows("otimizacao", report, falhasRows), empty: "Nenhuma sobreposição no período." },
    pendencias: { columns: columns.pendencia, rows: reportRows("pendencias", report, falhasRows), empty: "Nenhuma pendência operacional.", details: rowDetails.pendencias },
    "rotas-feitas": { columns: columns.rota, rows: reportRows("rotas-feitas", report, falhasRows), empty: "Sem rotas no período.", details: rowDetails["rotas-feitas"] },
    ocupacao: { columns: columns.ocupacao, rows: reportRows("ocupacao", report, falhasRows), empty: "Sem vans no período." },
    faturamento: { columns: columns.faturamento, rows: reportRows("faturamento", report, falhasRows), empty: "Sem pedidos no período.", details: rowDetails.faturamento },
    "rotas-motorista": { columns: columns.driver, rows: reportRows("rotas-motorista", report, falhasRows), empty: "Sem rotas por motorista." },
    comparativo: { columns: columns.comparativo, rows: reportRows("comparativo", report, falhasRows), empty: "Sem comparativo no período." },
    trilha: { columns: columns.trilha, rows: reportRows("trilha", report, falhasRows), empty: "Sem programações no período." },
    assinaturas: { columns: columns.assinatura, rows: reportRows("assinaturas", report, falhasRows), empty: "Sem assinaturas no período.", details: rowDetails.assinaturas },
    "assinaturas-detalhe": { columns: columns.assinaturaDetalhe, rows: reportRows("assinaturas-detalhe", report, falhasRows), empty: "Sem assinaturas no período." },
    disciplina: { columns: columns.disciplina, rows: reportRows("disciplina", report, falhasRows), empty: "Sem disciplina registrada." },
    recusas: { columns: columns.recusa, rows: reportRows("recusas", report, falhasRows), empty: "Sem recusas no período.", details: rowDetails.recusas },
    km: { columns: columns.km, rows: reportRows("km", report, falhasRows), empty: "Sem KM informado.", details: rowDetails.km },
    falhas: { columns: columns.falha, rows: reportRows("falhas", report, falhasRows), empty: "Sem falhas de automação." },
    comprovantes: { columns: columns.valor, rows: reportRows("comprovantes", report, falhasRows), empty: "Sem valores no período." },
    "funcionarios-ativos": { columns: columns.funcionario, rows: reportRows("funcionarios-ativos", report, falhasRows), empty: "Sem funcionários ativos." },
    "motoristas-ativos": { columns: columns.motorista, rows: reportRows("motoristas-ativos", report, falhasRows), empty: "Sem motoristas ativos." },
    "funcionarios-periodo": { columns: columns.funcionario, rows: reportRows("funcionarios-periodo", report, falhasRows), empty: "Sem funcionários novos." },
    "motoristas-periodo": { columns: columns.motorista, rows: reportRows("motoristas-periodo", report, falhasRows), empty: "Sem motoristas novos." },
  };

  // Export sai com as linhas já "expandidas": os campos do painel de detalhe
  // viram colunas extras no CSV/PDF (sem duplicar rótulo que já é coluna).
  const buildExportColumns = (config) => {
    if (!config?.details || !config.rows.length) return config.columns;
    const rotulosExistentes = new Set(config.columns.map((column) => column.label));
    const extras = config.details(config.rows[0])
      .map((item) => item.label)
      .filter((label) => !rotulosExistentes.has(label))
      .map((label) => ({
        key: `detalhe-${label}`,
        label,
        export: (row) => {
          const item = config.details(row).find((entry) => entry.label === label);
          const value = item?.value;
          return value == null || value === "" ? "—" : value;
        },
      }));
    return [...config.columns, ...extras];
  };

  const exportPdf = () => {
    if (!report) return;
    const win = window.open("", "_blank");
    if (!win) {
      showToast("Permita pop-ups para exportar o PDF.");
      return;
    }

    const config = reportConfig[selectedId];
    const content = selectedId === "resumo"
      ? `<div class="stats">
          <div class="stat"><b>${summary.rotas_feitas || 0}</b><span>Rotas operacionais</span></div>
          <div class="stat"><b>${summary.documentos_gerados || 0}</b><span>Documentos gerados</span></div>
          <div class="stat"><b>${summary.documentos_sem_rota || 0}</b><span>Docs sem rota</span></div>
          <div class="stat"><b>${summary.rotas_finalizadas || 0}</b><span>Finalizadas</span></div>
          <div class="stat"><b>${formatPercent(summary.percentual_assinaturas)}</b><span>Assinaturas</span></div>
        </div>`
      : buildTableHtml(selectedMeta.label, buildExportColumns(config), config.rows);

    win.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"/><title>${esc(selectedMeta.label)}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:28px;color:#111827}
        header{display:flex;justify-content:space-between;gap:20px;border-bottom:2px solid #f97316;padding-bottom:14px;margin-bottom:22px}
        h1{font-size:20px;margin:0}.meta{font-size:12px;color:#4b5563;margin-top:6px}
        .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.stat{border:1px solid #e5e7eb;border-radius:8px;padding:12px}.stat b{display:block;font-size:20px;color:#ea580c}.stat span{font-size:11px;color:#6b7280}
        table{width:100%;border-collapse:collapse;font-size:11px}th,td{border:1px solid #e5e7eb;padding:7px;vertical-align:top}th{background:#f3f4f6;text-align:left}
        h2{font-size:15px;margin:0 0 8px}@media print{.no-print{display:none}}
      </style></head><body>
        <header><div><div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;font-weight:600">${brandMark.replace('<svg ', '<svg width="26" height="26" ')} RotaFlex</div><h1>${esc(selectedMeta.label)}</h1><div class="meta">RotaFlex · ${esc(report.periodo?.label || "—")}<br/>Gerado em ${esc(formatDateTime(report.gerado_em))}</div></div><button class="no-print" onclick="window.print()">Imprimir / salvar PDF</button></header>
        ${content}
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  const exportCsv = () => {
    if (!report) return;
    if (selectedId === "resumo") {
      showToast("Selecione um relatório tabular para exportar CSV.");
      return;
    }
    const cfg = reportConfig[selectedId];
    if (!cfg || !cfg.rows.length) {
      showToast("Nenhum registro disponível para exportação.");
      return;
    }
    const csv = buildCsv(buildExportColumns(cfg), cfg.rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rotaflex-${selectedId}-${todayKey()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderBody = () => {
    if (!report) return null;
    if (selectedId === "resumo") {
      const topDriver = report.motoristas_rotas?.[0];
      return (
        <div style={{ padding: 18 }}>
          <h4 style={{ fontSize: '1rem', marginBottom: 14 }}>Viagens, passageiros e documentos</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 1.1fr) 1fr", gap: 14, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Metric label="Rotas operacionais" value={summary.rotas_feitas || 0} hint="viagens registradas" prominent />
              <Metric label="Documentos gerados" value={summary.documentos_gerados || 0} hint="total no período" prominent />
              <Metric label="Documentos de pedidos" value={summary.documentos_pedido || 0} hint="demanda de transporte" />
              <Metric label="Documentos das vans" value={summary.documentos_operacionais || 0} hint="documentos das vans" />
              <Metric label="Assinaturas" value={formatPercent(summary.percentual_assinaturas)} accent={Number(summary.percentual_assinaturas || 0) >= 80 ? "#22c55e" : "#fbbf24"} prominent />
              <Metric label="Finalizadas" value={summary.rotas_finalizadas || 0} accent="#22c55e" />
              <Metric label="Passageiros" value={summary.passageiros_planejados || 0} />
              <Metric label="Rotas documentadas" value={summary.rotas_com_documentos || 0} />
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <Insight label="Atenção" value={summary.pendencias_operacionais || 0} color={summary.pendencias_operacionais ? "#fbbf24" : "#22c55e"} text={summary.pendencias_operacionais ? "Há rotas/documentos que ainda precisam de fechamento." : "Nenhuma pendência operacional no período."} />
              {documentosInsight && (
                <Insight label="Documentos" value={documentosInsight.value} color="#38bdf8" text={documentosInsight.text} />
              )}
              <Insight label="Otimização" value={summary.sobreposicoes_regiao || 0} color={summary.sobreposicoes_regiao ? "#f97316" : "#22c55e"} text={summary.sobreposicoes_regiao ? "Existem regiões com múltiplas programações no mesmo dia." : "Sem sobreposição de região no período."} />
              <Insight label="Motorista com mais viagens" value={topDriver?.total_rotas || 0} color="#38bdf8" text={topDriver ? `${topDriver.motorista_nome} tem mais viagens no período.` : "Sem ranking de motorista no período."} />
            </div>
          </div>
          <h4 style={{ fontSize: '1rem', margin: "24px 0 14px" }}>Capacidade, valores e cadastros</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <Metric
              label="Ocupação das vans"
              value={formatPercent(summary.ocupacao_media_vans)}
              accent={getOcupacaoColor(summary.ocupacao_media_vans)}
              hint={`${summary.vans_passageiros_total || 0} passageiros / ${summary.vans_capacidade_total || 0} lugares`}
            />
            <Metric
              label="Faturamento pedidos"
              value={formatCurrency(summary.faturamento_pedidos_total)}
              hint={summary.pedidos_sem_valor ? `${summary.pedidos_sem_valor} ${summary.pedidos_sem_valor === 1 ? "pedido sem valor" : "pedidos sem valor"}` : "todos com valor"}
            />
            <Metric label="Com falha" value={summary.rotas_com_falha || 0} accent="#ef4444" />
            <Metric label="Recusas" value={summary.total_recusas || 0} accent="#f97316" />
            <Metric label="Falhas automação" value={summary.falhas_automacao || 0} accent="#ef4444" />
            <Metric label="Documentos sem rota" value={summary.documentos_sem_rota || 0} accent={summary.documentos_sem_rota ? "#fbbf24" : "#22c55e"} />
            <Metric label="Valor comprovantes" value={formatCurrency(summary.valor_comprovantes_total)} />
            <Metric label="Funcionários ativos" value={summary.funcionarios_ativos || 0} hint={`${summary.funcionarios_cadastrados || 0} novos ativos`} />
            <Metric label="Motoristas ativos" value={summary.motoristas_ativos || 0} hint={`${summary.motoristas_cadastrados || 0} novos ativos`} />
          </div>
        </div>
      );
    }

    const config = reportConfig[selectedId];
    return (
      <>
        {selectedId === "rotas-motorista" && <DriverChart key={selectedId} rows={config.rows} />}
        {config.details && (
          <div style={{ padding: "10px 16px", fontSize: '0.875rem', color: "var(--muted)", borderBottom: "1px solid var(--border)", background: "rgba(249,115,22,0.05)" }}>
            <Icon name="warning" /> Clique em uma linha para ver os detalhes.
          </div>
        )}
        <Table
          key={selectedId}
          tableKey={selectedId}
          columns={config.columns}
          rows={config.rows}
          empty={config.empty}
          renderDetails={config.details ? (row) => <DetailGrid items={config.details(row)} /> : undefined}
        />
      </>
    );
  };

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <PageTitle icon="report">Relatórios</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 4 }}>{report?.periodo?.label || "Período"} · {selectedMeta.label}</p>
        </div>
      </div>

      {toast && <Toast tone="error" inline onDismiss={() => setToast("")}>{toast}</Toast>}

      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(170px, 220px) 1fr auto", gap: 12, alignItems: "end" }}>
          <Select label="Período" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
            {PERIODS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </Select>
          {periodo === "custom" ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <Input type="date" label="Início" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              <Input type="date" label="Fim" value={fim} onChange={(e) => setFim(e.target.value)} />
            </div>
          ) : (
            <div style={{ fontSize: '0.9375rem', color: "var(--muted)", paddingBottom: 10 }}>
              {report?.gerado_em ? `Atualizado ${formatDateTime(report.gerado_em)}` : "—"}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: isMobile ? "stretch" : "flex-end" }}>
            <Btn variant="secondary" onClick={loadReport} disabled={loading} style={{ justifyContent: "center", flex: isMobile ? 1 : "initial" }}>{loading ? <Spinner /> : "Aplicar período"}</Btn>
            <Btn variant="secondary" onClick={exportPdf} disabled={!report || loading} style={{ justifyContent: "center", flex: isMobile ? 1 : "initial" }}>Exportar PDF</Btn>
            <Btn variant="secondary" onClick={exportCsv} disabled={!report || loading} style={{ justifyContent: "center", flex: isMobile ? 1 : "initial" }}>Exportar CSV</Btn>
          </div>
        </div>
      </Card>

      <ReportPicker reports={REPORTS} selectedId={selectedId} onSelect={selectReport} group={group} setGroup={setGroup} onGroupChange={changeGroup} report={report} falhasRows={falhasRows} isMobile={isMobile} />

      {loading && !report ? (
        <Card style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spinner /></Card>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden", minHeight: 360 }}>
          <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--border)", background: "linear-gradient(180deg, rgba(255,255,255,0.035), transparent)" }}>
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 900, margin: 0 }}>{selectedMeta.label}</h3>
            <p style={{ color: "var(--muted)", fontSize: '0.9375rem', margin: "5px 0 0" }}>{selectedMeta.description}</p>
          </div>
          <div style={{ opacity: loading ? 0.55 : 1 }}>
            {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spinner /></div> : renderBody()}
          </div>
        </Card>
      )}
    </div>
  );
}
